import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createDeal, getDeals, getDealDetail, deleteDeal, updateDeal, sendDealSMS } from "../api.js";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../contexts/NotificationContext.js";

export const useGetDealDetail = (id, tab, tenantId) => {
  const { data, isLoading, isError, error, isSuccess, isFetching } = useQuery({
    queryKey: ['deal', id, tab, tenantId],
    queryFn: () => getDealDetail(id, tab, tenantId),
    enabled: !!id && !!tab,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  return { data, isLoading, isSuccess, isError, error, isFetching };
};
export const useGetDeals = (search, status, page, pageSize, startDate, endDate, worksheetType, enabled = true) => {
  const statusKey = Array.isArray(status)
    ? [...status].map(String).sort().join(",")
    : String(status ?? "");
  const worksheetKey = Array.isArray(worksheetType)
    ? [...worksheetType].map(String).sort().join(",")
    : String(worksheetType ?? "");
  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ['deals', { search, statusKey, page, pageSize, startDate, endDate, worksheetKey }],
    queryFn: () => getDeals(search, status, page, pageSize, startDate, endDate, worksheetType),
    enabled,
    staleTime: 1000 * 60 * 5,
    keepPreviousData: true,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return { data, isLoading, isSuccess, isError, error };
};


export const useDeleteDeal = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id }) => deleteDeal(id),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["deals"] });

      // Show success notification
      showNotification('success', 'Deal deleted successfully!');

      // Navigate to inventory list
      // navigate('/search-inventory');
    },
    onError: (error) => {
      console.error("Error deleting inventory:", error);

      // Show error notification
      let errorMessage = 'Error deleting inventory. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useCreateDeal = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: (data) => createDeal(data),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["deals"] });
      await queryClient.invalidateQueries({ queryKey: ["deal"] });

      showNotification('success', 'Inventory created successfully!');

      // Navigate immediately without setTimeout
      navigate(`/deals/${data?.deal_id}`);
      // navigate(`/deals`);
    },
    onError: (error) => {
      console.error("Error creating inventory:", error);

      // Show error notification
      let errorMessage = 'Error creating inventory. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });

  return mutation;
};
export const useUpdateDeal = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id, data, tenantId }) => updateDeal(id, data, tenantId),
    onSuccess: async () => {
      // Invalidate all deal queries to ensure all tabs are updated
      await queryClient.invalidateQueries({ queryKey: ["deal"] });

      // Show success notification
      showNotification('success', 'Deal updated successfully!');
    },
    onError: (error) => {
      console.error("Error updating inventory:", error);

      // Show error notification
      let errorMessage = 'Error updating inventory. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useSendDealSMS = () => {
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (dealId) => sendDealSMS(dealId),
    onSuccess: (data) => {
      // Show success notification
      showNotification('success', 'SMS authorization sent successfully!');
    },
    onError: (error) => {
      console.error("Error sending SMS:", error);

      // Show error notification
      let errorMessage = 'Error sending SMS. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};